import * as React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Grid,
} from '@material-ui/core';
import EditStore from './EditStore';
import type { IStoreDetails } from './StoreInterfaces';
import type ICompanyContactDetails from '../company/CompanyInterfaces';
import { emptyStoreDetails } from '../adminHelpers';

const Store: React.FC<unknown> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const companyIdParam = searchParams.get('companyId');
  const storeIdParam = searchParams.get('storeId');

  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const [editingHidden, setEditingHidden] = React.useState<boolean>(false);
  const [storeFormValues, setStoreFormValues] = React.useState<IStoreDetails>(emptyStoreDetails(storeIdParam));
  const handleBackToAllStoresClicked = () => {
    navigate('/stores');
  };

  const handleSaveBtnClicked = () => {
    console.log(storeFormValues);
    setIsFetching(true);
    axios.post<IStoreDetails>('/store/upsert', {
      CompanyName: storeFormValues.companyName,
      StoreID: storeIdParam,
      Name: storeFormValues.name,
      StoreNumber: storeFormValues.name,
      Email: storeFormValues.email ?? '',
      Address1: storeFormValues.address1,
      City: storeFormValues.city,
      State: storeFormValues.state,
      ZipCode: storeFormValues.zipCode,
      Tags: storeFormValues.tags,
      StoreOptions: storeFormValues.storeOptions,
    })
      .then(() => {
        if (editingHidden) {
          enqueueSnackbar(`Added Store: ${storeFormValues.name}.`);
        } else {
          enqueueSnackbar(`Updated Store: ${storeFormValues.name}.`);
        }
        navigate('/stores');
      })
      .catch((error: AxiosError) => {
        if (error?.message) {
          enqueueSnackbar(`Unable to save store data: ${error?.message}`, { persist: false, variant: 'error' });
        }
      })
      .finally(() => {
        setIsFetching(false);
        setEditingHidden(true);
      });
  };

  React.useEffect(() => {
    if (storeIdParam || companyIdParam) {
      // For store add.
      if (companyIdParam) {
        setEditingHidden(true);
        axios.get<ICompanyContactDetails>('/company/GetCompany', {
          params: {
            companyId: companyIdParam,
          },
        })
          .then((result) => {
            if (result.data) {
              setStoreFormValues({ ...storeFormValues, companyName: result.data.name });
            }
          })
          .catch((error: AxiosError) => {
            if (error?.message) {
              enqueueSnackbar(`Unable to find company name for store: ${storeFormValues.name}.`, { persist: false, variant: 'error' });
            }
          });
      }
      if (storeIdParam) {
        axios.get<IStoreDetails>('/store/GetStore', {
          params: {
            storeId: storeIdParam,
          },
        })
          .then((result) => {
            if (result.data) {
              setStoreFormValues(result.data);
            }
          })
          .catch((error: AxiosError) => {
            if (error?.message) {
              enqueueSnackbar('Unable to find store.', { persist: false, variant: 'error' });
            }
          });
      }
    }
  // only want to run on initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid lg={8} xs={12}>
      <EditStore
        backToAllStores={handleBackToAllStoresClicked}
        StoreFormValues={storeFormValues}
        editingHidden={editingHidden}
        handleSaveBtnClicked={handleSaveBtnClicked}
        isFetching={isFetching}
        setStoreFormValues={setStoreFormValues}
      />
    </Grid>
  );
};

export default Store;
